import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  API,
  Field2 as Field,
  VibeToggle,
  color,
} from 'vibeguide';
import Grid from '@mui/material/Grid';
import RotationDropdown from '../../../Sidebar/RotationDropdown';
import '../../../Sidebar/DisplayInformation.scss';

const DEFAULT_DISPLAY_SETTINGS = {
  resolution: { name: '', size: [0, 0] },
  screenDimensions: ['', ''],
  screenDiagonal: '',
  screenRotation: 0,
  contentRotation: 0,
  advancedEnabled: false,
  displayAreaDimensions: [0, 0],
  displayAreaPosition: [0, 0],
};

function ProfileDisplayInformation({
  className = '',
  displaySettings: displaySettingsProp,
  canEdit = false,
  isAdmin = false,
  onUpdate = () => {},
}) {
  const canAdminEdit = isAdmin && canEdit;
  const isDisabled = !canAdminEdit;

  const displaySettings = {
    ...DEFAULT_DISPLAY_SETTINGS,
    ...(displaySettingsProp || {}),
  };

  const {
    resolution = { name: '', size: [0, 0] },
    screenDimensions = ['', ''],
    screenDiagonal = '',
    screenRotation = 0,
    contentRotation = 0,
    advancedEnabled = false,
    displayAreaDimensions = [0, 0],
    displayAreaPosition = [0, 0],
  } = displaySettings;

  const resolutionName = resolution?.name || '';
  const resolutionSize = resolution?.size || [0, 0];

  const patchDisplaySettings = (patch) => {
    onUpdate({
      displaySettings: {
        ...displaySettings,
        ...patch,
      },
    });
  };

  const patchTupleField = (fieldName, index, rawValue) => {
    const current = displaySettings[fieldName] || [0, 0];
    const next = [...current];
    next[index] = rawValue;
    patchDisplaySettings({
      [fieldName]: next,
    });
  };

  const onTupleChange = (fieldName, index) => (e) => {
    patchTupleField(fieldName, index, e.target.value);
  };

  const onChangeScreenDiagonal = (e) => {
    patchDisplaySettings({
      screenDiagonal: e.target.value,
    });
  };

  const onChangeRotation = ({ name, value }) => {
    patchDisplaySettings({
      [name]: value,
    });
  };

  const onToggleAdvanced = () => {
    patchDisplaySettings({
      advancedEnabled: !advancedEnabled,
    });
  };

  const onChangeResolution = (e) => {
    const { data } = e;
    const size = data?.size || [0, 0];
    const isCustom = Boolean(data?.custom);

    const next = isCustom
      ? {
        name: `Custom (${size[0]} x ${size[1]})`,
        size,
        custom: true,
      }
      : {
        _id: data?._id,
        name: data?.name || '',
        size,
      };

    patchDisplaySettings({
      resolution: next,
    });
  };

  const onChangeCustomResolutionWidth = (e) => {
    const next = [e.target.value || 0, resolutionSize?.[1] || 0];
    patchDisplaySettings({
      resolution: {
        name: `Custom (${next[0]} x ${next[1]})`,
        size: next,
        custom: true,
      },
    });
  };

  const onChangeCustomResolutionHeight = (e) => {
    const next = [resolutionSize?.[0] || 0, e.target.value || 0];
    patchDisplaySettings({
      resolution: {
        name: `Custom (${next[0]} x ${next[1]})`,
        size: next,
        custom: true,
      },
    });
  };

  const clickDisplayInput = (e) => {
    e.stopPropagation();
  };

  const checkBlockBlur = (e) => {
    if (e.relatedTarget !== null) {
      return e.relatedTarget.classList.contains('display-input-width')
        || e.relatedTarget.classList.contains('display-input-height');
    }
    return false;
  };

  const customResolutionWidth = resolutionSize?.[0] ?? 0;
  const customResolutionHeight = resolutionSize?.[1] ?? 0;
  const isCustomResolution = Boolean(resolution?.custom)
    || (typeof resolutionName === 'string' && resolutionName.toLowerCase().startsWith('custom'));

  return (
    <div
      className={classNames('ProfileDisplayInformation', 'DisplayInformation', className)}
    >
      <div className="display-information-header">
        <div className="display-information-title">
          Display Information
        </div>

        <VibeToggle
          className="advanced-toggle"
          label="Advanced Display Settings"
          height={12}
          color={color.success}
          checked={advancedEnabled}
          disabled={isDisabled}
          testId="location-profile-advanced-display-toggle"
          onChange={onToggleAdvanced}
        />
      </div>

      <Grid spacing={2} container>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Field
            type="text"
            label="Screen Width"
            name="screenWidth"
            placeholder="Screen Width"
            innerLabelProps={{
              text: 'inches',
            }}
            labelProps={{
              tooltip: 'Physical width of display',
              informationIcon: true,
              tooltipPlacement: 'right',
            }}
            value={screenDimensions[0] ?? ''}
            onChange={onTupleChange('screenDimensions', 0)}
            disabled={isDisabled}
            tabIndex={10}
            testId="location-profile-screen-width-input"
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Field
            type="text"
            label="Screen Height"
            name="screenHeight"
            placeholder="Screen Height"
            innerLabelProps={{
              text: 'inches',
            }}
            labelProps={{
              tooltip: 'Physical height of display',
              informationIcon: true,
              tooltipPlacement: 'right',
            }}
            value={screenDimensions[1] ?? ''}
            onChange={onTupleChange('screenDimensions', 1)}
            disabled={isDisabled}
            tabIndex={11}
            testId="location-profile-screen-height-input"
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Field
            type="text"
            label="Screen Diagonal"
            name="screenDiagonal"
            placeholder="Screen Diagonal"
            innerLabelProps={{
              text: 'inches',
            }}
            labelProps={{
              tooltip: 'Screen size',
              informationIcon: true,
              tooltipPlacement: 'right',
            }}
            value={screenDiagonal ?? ''}
            onChange={onChangeScreenDiagonal}
            disabled={isDisabled}
            tabIndex={12}
            testId="location-profile-screen-diagonal-input"
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Field
            type="dropdown"
            label="Resolution"
            name="resolution"
            value={resolutionName}
            tabIndex={13}
            testId="location-profile-resolution-dropdown"
            labelProps={{
              tooltip: 'Maximum display area',
              informationIcon: true,
              tooltipPlacement: 'right',
            }}
            dropdownProps={{
              type: 'screen resolution',
              attr: 'name',
              fetch: API.ScreenResolution.list,
              canCreateCustomItem: true,
              blockBlur: checkBlockBlur,
              customHTML: (
                <div className="custom-content">
                  <div className="text">
                    Custom
                  </div>
                  <div className="display-custom-inputs">
                    <div className="display-custom-width">
                      <input
                        type="text"
                        value={customResolutionWidth}
                        className="display-input-width"
                        onClick={clickDisplayInput}
                        onChange={onChangeCustomResolutionWidth}
                      />
                      <span className="display-input-placeholder">W</span>
                    </div>
                    <div className="display-custom-height">
                      <input
                        type="text"
                        value={customResolutionHeight}
                        className="display-input-height"
                        onClick={clickDisplayInput}
                        onChange={onChangeCustomResolutionHeight}
                      />
                      <span className="display-input-placeholder">H</span>
                    </div>
                  </div>
                </div>
              ),
              customItem: isCustomResolution ? {
                active: true,
                name: resolutionName || 'Custom',
                size: resolutionSize,
                length: 2,
                custom: true,
              } : {
                active: true,
                name: 'Custom',
                size: [0, 0],
                length: 2,
                custom: true,
              },
            }}
            disabled={isDisabled}
            required
            onChange={onChangeResolution}
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <RotationDropdown
            name="screenRotation"
            label="Screen Rotation"
            labelTooltip="How the display is positioned"
            value={screenRotation}
            required
            disabled={isDisabled}
            tabIndex={14}
            testId="location-profile-screen-rotation-dropdown"
            onChange={onChangeRotation}
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <RotationDropdown
            name="contentRotation"
            label="Content Rotation"
            labelTooltip="How the content should be rotated"
            value={contentRotation}
            required
            disabled={isDisabled}
            tabIndex={15}
            testId="location-profile-content-rotation-dropdown"
            onChange={onChangeRotation}
          />
        </Grid>
      </Grid>

      {advancedEnabled && (
        <div className="advanced-display-settings">
          <div className="advanced-display-settings-title">
            Advanced Display Settings
          </div>

          <Grid spacing={2} container>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Field
                type="text"
                label="Display Area Width"
                name="displayAreaWidth"
                placeholder="Display Area Width"
                innerLabelProps={{
                  text: 'Pixels',
                }}
                labelProps={{
                  tooltip: 'Where the media will play',
                  informationIcon: true,
                  tooltipPlacement: 'right',
                }}
                value={displayAreaDimensions[0] ?? 0}
                onChange={onTupleChange('displayAreaDimensions', 0)}
                disabled={isDisabled}
                tabIndex={16}
                testId="location-profile-display-area-width-input"
              />
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Field
                type="text"
                label="Display Area Height"
                name="displayAreaHeight"
                placeholder="Display Area Height"
                innerLabelProps={{
                  text: 'Pixels',
                }}
                labelProps={{
                  tooltip: 'Where the media will play',
                  informationIcon: true,
                  tooltipPlacement: 'right',
                }}
                value={displayAreaDimensions[1] ?? 0}
                onChange={onTupleChange('displayAreaDimensions', 1)}
                disabled={isDisabled}
                tabIndex={17}
                testId="location-profile-display-area-height-input"
              />
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Field
                type="text"
                label="Horizontal Offset"
                name="horizontalOffset"
                placeholder="Horizontal Offset"
                innerLabelProps={{
                  text: 'Pixels',
                }}
                labelProps={{
                  tooltip: 'Where the display area is positioned within the resolution',
                  informationIcon: true,
                  tooltipPlacement: 'right',
                }}
                value={displayAreaPosition[0] ?? 0}
                onChange={onTupleChange('displayAreaPosition', 0)}
                disabled={isDisabled}
                tabIndex={18}
                testId="location-profile-horizontal-offset-input"
              />
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Field
                type="text"
                label="Vertical Offset"
                name="verticalOffset"
                placeholder="Vertical Offset"
                innerLabelProps={{
                  text: 'Pixels',
                }}
                labelProps={{
                  tooltip: 'Where the display area is positioned within the resolution',
                  informationIcon: true,
                  tooltipPlacement: 'right',
                }}
                value={displayAreaPosition[1] ?? 0}
                onChange={onTupleChange('displayAreaPosition', 1)}
                disabled={isDisabled}
                tabIndex={19}
                testId="location-profile-vertical-offset-input"
              />
            </Grid>
          </Grid>
        </div>
      )}
    </div>
  );
}

const numericPair = PropTypes.arrayOf(PropTypes.oneOfType([
  PropTypes.number,
  PropTypes.string,
]));

ProfileDisplayInformation.propTypes = {
  className: PropTypes.string,
  displaySettings: PropTypes.shape({
    resolution: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      size: numericPair,
      custom: PropTypes.bool,
    }),
    screenDimensions: numericPair,
    screenDiagonal: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    screenRotation: PropTypes.number,
    contentRotation: PropTypes.number,
    advancedEnabled: PropTypes.bool,
    displayAreaDimensions: numericPair,
    displayAreaPosition: numericPair,
  }),
  canEdit: PropTypes.bool,
  isAdmin: PropTypes.bool,
  onUpdate: PropTypes.func,
};

export default ProfileDisplayInformation;
